'use client';
import React, { useRef, useState, useEffect } from 'react';
import { FaCode, FaMobileAlt, FaShieldAlt, FaTachometerAlt, FaBullhorn, FaCookieBite, FaPlusCircle, FaTimesCircle, FaServer } from 'react-icons/fa';
import { twMerge } from 'tailwind-merge';
import { motion } from 'framer-motion';

// --- Data Structure ---
const services = [
  // Top Tier: Core Offerings
  {
    id: 'custom-web',
    icon: <FaCode size={36} className="text-indigo-400" />,
    title: 'Web na míru',
    description: 'Od jednoduchých stránek po komplexní portály. Tvoříme weby, které přesně odpovídají vašim potřebám a cílům.',
    longDescription: 'Naše řešení jsou postavena na moderních technologiích, které zaručují flexibilitu a škálovatelnost. Každý projekt je pro nás unikátní výzvou.',
    position: { top: '10%', left: '15%' },
  },
  {
    id: 'responsive',
    icon: <FaMobileAlt size={36} className="text-purple-400" />,
    title: 'Responzivní Design',
    description: 'Zajistíme, aby váš web vypadal a fungoval perfektně na všech zařízeních – od mobilů po velké monitory.',
    position: { top: '10%', left: '60%' },
  },
  // Middle Tier: Technical Pillars
  {
    id: 'security',
    icon: <FaShieldAlt size={36} className="text-green-400" />,
    title: 'Bezpečnost a Stabilita',
    description: 'Klademe důraz na bezpečný kód a stabilní provoz, aby vaše data i uživatelé byli vždy v bezpečí.',
    longDescription: 'Implementujeme osvědčené bezpečnostní postupy, od ochrany proti XSS a CSRF útokům až po pravidelné bezpečnostní audity a aktualizace.',
    position: { top: '45%', left: '70%' },
  },
  {
    id: 'hosting',
    icon: <FaServer size={36} className="text-teal-400" />,
    title: 'Hosting a Správa',
    description: 'Váš web nejen vytvoříme, ale postaráme se i o jeho spolehlivý hosting, údržbu a pravidelné aktualizace.',
    longDescription: 'Nabízíme vlastní hostingové řešení. Váš web tak bude v bezpečí a pod naší neustálou kontrolou pro maximální výkon.',
    position: { top: '60%', left: '35%' },
  },
  {
    id: 'speed',
    icon: <FaTachometerAlt size={36} className="text-sky-400" />,
    title: 'Rychlost a Optimalizace',
    description: 'Optimalizujeme rychlost načítání, protože víme, že každá milisekunda se počítá pro udržení návštěvníka.',
    position: { top: '45%', left: '5%' },
  },
  // Bottom Tier: Business Growth
  {
    id: 'seo',
    icon: <FaBullhorn size={36} className="text-amber-400" />,
    title: 'Marketing a SEO',
    description: 'Pomůžeme vám být vidět. Připravíme váš web na úspěšné marketingové kampaně a vysoké pozice ve vyhledávačích.',
    position: { top: '85%', left: '15%' }, // Adjusted
  },
  {
    id: 'cookies',
    icon: <FaCookieBite size={36} className="text-rose-400" />,
    title: 'Minimum otravných cookies',
    description: 'Respektujeme soukromí uživatelů. Implementujeme jen nezbytné nástroje a vyhýbáme se zbytečným cookie lištám.',
    position: { top: '85%', left: '60%' }, // Adjusted
  },
];

// --- Star Jump Map Connections (Corrected) ---
const connections = [
  // Tier 0 -> Tier 1
  ['black-hole', 'custom-web'],
  ['black-hole', 'responsive'],

  // Tier 0 -> Tier 2
  ['black-hole', 'hosting'],

  // Jumps within Tier 1
  ['custom-web', 'responsive'],

  // Tier 1 -> Tier 2
  ['custom-web', 'speed'],
  ['responsive', 'security'],
  ['custom-web', 'hosting'],

  // Jumps within Tier 2
  ['speed', 'hosting'],
  ['hosting', 'security'],

  // Tier 2 -> Tier 3
  ['speed', 'seo'],
  ['security', 'seo'],
  ['hosting', 'cookies'],

  // Jumps within Tier 3
  ['seo', 'cookies'],
];

// --- Main Component ---
const Services = () => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [lines, setLines] = useState<Array<{ x1: number; y1: number; x2: number; y2: number }>>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number>();

  // --- Line Calculation Logic ---
  useEffect(() => {
    const calculateLines = () => {
      if (!containerRef.current) return;
      
      const newLines = connections.map(([startId, endId]) => {
        const startEl = containerRef.current?.querySelector(`#card-${startId}`);
        const endEl = containerRef.current?.querySelector(`#card-${endId}`);

        if (startEl && endEl) {
          const containerRect = containerRef.current!.getBoundingClientRect();
          const startRect = startEl.getBoundingClientRect();
          const endRect = endEl.getBoundingClientRect();

          return {
            x1: startRect.left - containerRect.left + startRect.width / 2,
            y1: startRect.top - containerRect.top + startRect.height / 2,
            x2: endRect.left - containerRect.left + endRect.width / 2,
            y2: endRect.top - containerRect.top + endRect.height / 2,
          };
        }
        return null;
      }).filter(Boolean) as Array<{ x1: number; y1: number; x2: number; y2: number }>;
      
      setLines(newLines);
    };

    const animateLines = () => {
      calculateLines();
      animationFrameRef.current = requestAnimationFrame(animateLines);
    };

    animateLines();

    const animationTimeout = setTimeout(() => {
      cancelAnimationFrame(animationFrameRef.current!);
    }, 500);

    window.addEventListener('resize', calculateLines);
    
    return () => {
      window.removeEventListener('resize', calculateLines);
      cancelAnimationFrame(animationFrameRef.current!);
      clearTimeout(animationTimeout);
    };
  }, [expandedId]);

  return (
    <section id="services" className="py-20">
      <div className="container mx-auto px-6">
        <h2 className="text-4xl font-bold text-center text-white mb-12">
          Galaxie služeb
        </h2>
        
        <div ref={containerRef} className="hidden md:block relative h-[1200px] w-full">
          <motion.svg className="absolute top-0 left-0 w-full h-full" style={{ zIndex: 0 }}>
            <g>
              {lines.map((line, index) => (
                <motion.line
                  key={index}
                  x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2}
                  stroke="rgba(129, 140, 248, 0.3)"
                  strokeWidth="2"
                  transition={{ duration: 0.1 }}
                />
              ))}
            </g>
          </motion.svg>

          {/* --- Black Hole --- */}
          <motion.div
            id="card-black-hole"
            className="absolute flex items-center justify-center"
            style={{
              top: '40%', left: '50%',
              width: '10rem', height: '10rem',
              translateX: '-50%', translateY: '-50%',
              zIndex: 1,
            }}
          >
            <div className="text-8xl font-thin subtle-pulse select-none">Δ</div>
          </motion.div>

          {services.map((service) => (
            <motion.div
              id={`card-${service.id}`}
              key={service.id}
              layout
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              animate={{ 
                opacity: expandedId && expandedId !== service.id ? 0.6 : 1,
                scale: expandedId && expandedId !== service.id ? 0.95 : 1,
              }}
              className={twMerge(
                'glass-card p-6 text-white flex flex-col absolute cursor-pointer',
                expandedId === service.id ? 'shadow-purple-500/50' : 'shadow-lg'
              )}
              style={{
                top: service.position.top,
                left: service.position.left,
                width: '22rem',
                zIndex: expandedId === service.id ? 10 : 1,
              }}
              onClick={() => setExpandedId(expandedId === service.id ? null : service.id)}
            >
              <motion.div layout="position" className="text-center">
                <div className="mb-4 inline-block">{service.icon}</div>
                <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                <p className="text-slate-300 text-sm">{service.description}</p>
              </motion.div>

              {expandedId === service.id && service.longDescription && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.3 }}
                  className="mt-4 border-t border-white/20 pt-4 text-center"
                >
                  <p className="text-slate-300 text-sm">
                    {service.longDescription}
                  </p>
                </motion.div>
              )}
              
              {service.longDescription && (
                 <motion.div 
                    layout="position" 
                    className="w-full text-center mt-auto pt-4"
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                 >
                    {expandedId === service.id 
                      ? <FaTimesCircle size={20} className="text-purple-400 mx-auto" /> 
                      : <FaPlusCircle size={20} className="text-purple-400 mx-auto" />
                    }
                 </motion.div>
              )}
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:hidden gap-8">
          {services.map((service) => (
            <div key={service.id} className="glass-card p-8 text-center text-white flex flex-col items-center">
              <div className="mb-6">{service.icon}</div>
              <h3 className="text-2xl font-bold mb-4">{service.title}</h3>
              <p className="text-slate-300 flex-grow">{service.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
